import type {
  AccountState,
  IndexedPoolEvent,
  MerkleStateCheckpoint,
  ReplayPosition,
  Groth16Proof,
} from "@tonresistor/zkresistor-sdk";
import { Address } from "@ton/core";

export const GATEWAY_SCHEMA_VERSION = 1 as const;

export interface LegacyPoolManifest {
  address: string;
  factoryAddress: string;
  factoryCodeHash: string;
  kind: "jetton";
  jettonMaster: string;
  denomination: string;
}

export interface ProtocolManifest {
  schemaVersion: 1;
  network: "mainnet";
  factoryAddress: string;
  treeDepth: 20;
  treeCapacity: 1_048_576;
  codeHashes: {
    factory: string;
    pool: string;
    tonPool: string;
  };
  sdk: {
    package: "@tonresistor/zkresistor-sdk";
    version: "2.0.1";
  };
  legacyPools: readonly LegacyPoolManifest[];
  artifacts: {
    revision: string;
    files: Record<string, string>;
  };
}

export interface RuntimeManifest {
  schemaVersion: 1;
  apiBaseUrl: string;
  protocolManifestSha256: string;
  indexerSigningKey: {
    algorithm: "Ed25519";
    keyId: string;
    publicKeyBase64url: string;
  };
}

export interface SignedEnvelope {
  schemaVersion: 1;
  domain: string;
  algorithm: "Ed25519";
  keyId: string;
  payloadSha256: string;
  payloadBase64url: string;
  signatureBase64url: string;
}

export type PoolKind = "jetton" | "ton";

export interface ChainView {
  masterchainSeqno: number;
  blockId: string;
  observedAt: string;
}

export interface CatalogPoolAccount {
  address: string;
  kind: PoolKind;
  jettonMaster: string | null;
  privacySet: number;
  estimatedTvlNanoGram: string | null;
  account: AccountState;
  metadata: JettonMetadata | null;
}

export interface CatalogResponse {
  schemaVersion: 1;
  schema: "zkresistor.catalog.v1";
  protocolManifestSha256: string;
  chain: "mainnet";
  view: ChainView;
  factory: {
    address: string;
    account: AccountState;
  };
  pools: CatalogPoolAccount[];
}

export interface SerializedSyncTarget {
  poolAddress: string;
  nextIndex: number;
  withdrawalCount: number;
  currentRoot: string;
}

export interface ContentAddressedRef {
  url: string;
  sha256: string;
  byteLength: number;
}

export interface DeltaChunkRef extends ContentAddressedRef {
  fromExclusive: ReplayPosition;
  scannedThrough: ReplayPosition;
  eventCount: number;
}

export interface MerkleDeltaDocument {
  schemaVersion: 1;
  schema: "zkresistor.merkle-delta.v1";
  poolAddress: string;
  fromExclusive: ReplayPosition;
  scannedThrough: ReplayPosition;
  checkpoint: Pick<MerkleStateCheckpoint, "nextIndex" | "withdrawalCount" | "currentRoot">;
  events: readonly IndexedPoolEvent[];
}

export interface PoolViewResponse {
  schemaVersion: 1;
  schema: "zkresistor.pool-view.v1";
  protocolManifestSha256: string;
  viewId: string;
  view: ChainView;
  poolAddress: string;
  kind: PoolKind;
  checkpoint: {
    position: {
      blockSeqno: number;
      transactionLt: string;
      eventIndex: number;
    };
    nextIndex: number;
    withdrawalCount: number;
    currentRoot: string;
    commitmentSeenRoots: string[];
    nullifierSpentRoots: string[];
  };
  account: AccountState;
  history: {
    baseSnapshot: null | {
      checkpoint: PoolViewResponse["checkpoint"];
      chunks: ContentAddressedRef[];
    };
    deltaChunks: DeltaChunkRef[];
  };
}

export interface FactoryLookupRequest {
  kind: PoolKind;
  denomination: string;
  jettonMaster?: string;
}

export interface FactoryLookupResponse {
  registeredAddress: string | null;
  pending: boolean;
}

export interface JettonMetadata {
  symbol: string;
  decimals: number;
  decimalsVerified: boolean;
  name: string | null;
  image: string | null;
}

export interface JettonMetadataResponse {
  schemaVersion: 1;
  schema: "zkresistor.jetton-metadata.v1";
  view: ChainView;
  protocolManifestSha256: string;
  jettonMaster: string;
  metadata: JettonMetadata;
}

export interface ProtocolApi {
  catalog(signal?: AbortSignal): Promise<CatalogResponse>;
  openPoolView(
    poolAddress: string,
    kind: PoolKind,
    signal?: AbortSignal,
  ): Promise<PoolViewResponse>;
  jettonMetadata(jettonMaster: string, signal?: AbortSignal): Promise<JettonMetadata>;
  jettonWalletAddress(jettonMaster: string, owner: string, signal?: AbortSignal): Promise<string>;
  relayWithdrawal(request: RelayWithdrawalRequest, signal?: AbortSignal): Promise<RelayWithdrawalResponse>;
  relayWithdrawalStatus(
    jobId: string,
    expected: RelayWithdrawalBinding,
    signal?: AbortSignal,
  ): Promise<RelayWithdrawalResponse>;
  relayWithdrawalLookup(
    expected: RelayWithdrawalBinding,
    signal?: AbortSignal,
  ): Promise<RelayWithdrawalResponse>;
}

export interface RelayWithdrawalBinding {
  poolAddress: string;
  nullifierHash: string;
  recipient: string;
}

export interface RelayWithdrawalRequest extends RelayWithdrawalBinding {
  payloadBoc: string;
  proof?: Groth16Proof;
}

export type RelayWithdrawalStatus = "validating" | "submitted" | "confirmed" | "failed";

export interface RelayWithdrawalResponse {
  jobId: string;
  status: RelayWithdrawalStatus;
  poolAddress: string;
  nullifierHash: string;
  recipient: string;
  transactionHash?: string;
  submittedAt: string;
  updatedAt: string;
}

export interface JettonWalletResponse {
  schemaVersion: 1;
  schema: "zkresistor.jetton-wallet.v1";
  view: ChainView;
  protocolManifestSha256: string;
  jettonMaster: string;
  owner: string;
  walletAddress: string;
}

export function parseProtocolManifest(value: unknown): ProtocolManifest {
  const record = asRecord(value, "protocol manifest");
  const codeHashes = asRecord(record.codeHashes, "protocol codeHashes");
  const sdk = asRecord(record.sdk, "protocol sdk");
  const artifacts = asRecord(record.artifacts, "protocol artifacts");
  const files = asStringRecord(artifacts.files, "protocol artifact hashes");
  if (!Array.isArray(record.legacyPools) || record.legacyPools.length > 16) {
    throw new Error("Protocol legacy Pool allowlist is invalid");
  }
  const legacyPools = record.legacyPools.map((value, index): LegacyPoolManifest => {
    const pool = asRecord(value, `legacy Pool ${index}`);
    if (pool.kind !== "jetton") throw new Error("Only legacy Jetton Pools are supported");
    const denomination = asString(pool.denomination, `legacy Pool ${index} denomination`);
    if (!/^[1-9][0-9]*$/.test(denomination) || BigInt(denomination) >= 1n << 120n) {
      throw new Error(`legacy Pool ${index} denomination is invalid`);
    }
    return {
      address: asTonAddress(pool.address, `legacy Pool ${index} address`),
      factoryAddress: asTonAddress(pool.factoryAddress, `legacy Pool ${index} Factory`),
      factoryCodeHash: asHash(pool.factoryCodeHash, `legacy Pool ${index} Factory code hash`),
      kind: "jetton",
      jettonMaster: asTonAddress(pool.jettonMaster, `legacy Pool ${index} Jetton master`),
      denomination,
    };
  });
  if (new Set(legacyPools.map((pool) => pool.address)).size !== legacyPools.length) {
    throw new Error("Protocol legacy Pool allowlist contains duplicates");
  }
  const requiredArtifacts = [
    "hasher.wasm",
    "insert.wasm",
    "insert_final.zkey",
    "insert_vk.json",
    "withdraw.wasm",
    "withdraw_final.zkey",
    "withdraw_vk.json",
  ];
  if (
    record.schemaVersion !== 1 ||
    record.network !== "mainnet" ||
    record.treeDepth !== 20 ||
    record.treeCapacity !== 1_048_576 ||
    sdk.package !== "@tonresistor/zkresistor-sdk" ||
    sdk.version !== "2.0.1"
  ) throw new Error("Unsupported protocol manifest");
  if (
    Object.keys(files).length !== requiredArtifacts.length ||
    requiredArtifacts.some((name) => files[name] === undefined)
  ) throw new Error("Protocol manifest must contain the exact frozen circuit artifact set");
  return {
    schemaVersion: 1,
    network: "mainnet",
    factoryAddress: asTonAddress(record.factoryAddress, "factoryAddress"),
    treeDepth: 20,
    treeCapacity: 1_048_576,
    codeHashes: {
      factory: asHash(codeHashes.factory, "Factory code hash"),
      pool: asHash(codeHashes.pool, "Pool code hash"),
      tonPool: asHash(codeHashes.tonPool, "TonPool code hash"),
    },
    sdk: { package: "@tonresistor/zkresistor-sdk", version: "2.0.1" },
    legacyPools,
    artifacts: {
      revision: asString(artifacts.revision, "artifact revision"),
      files,
    },
  };
}

export function legacyPoolForAddress(
  manifest: ProtocolManifest,
  poolAddress: string,
): LegacyPoolManifest | undefined {
  const address = Address.parse(poolAddress);
  return manifest.legacyPools.find((pool) => Address.parse(pool.address).equals(address));
}

export function expectedPoolFactory(
  manifest: ProtocolManifest,
  poolAddress: string,
): { address: string; codeHash: string } {
  const legacy = legacyPoolForAddress(manifest, poolAddress);
  return legacy === undefined
    ? { address: manifest.factoryAddress, codeHash: manifest.codeHashes.factory }
    : { address: legacy.factoryAddress, codeHash: legacy.factoryCodeHash };
}

export function parseRuntimeManifest(value: unknown): RuntimeManifest {
  const record = asRecord(value, "runtime manifest");
  const key = asRecord(record.indexerSigningKey, "indexer signing key");
  if (record.schemaVersion !== 1 || key.algorithm !== "Ed25519") {
    throw new Error("Unsupported runtime manifest");
  }
  const apiBaseUrl = asString(record.apiBaseUrl, "apiBaseUrl");
  if (!(
    (/^\/(?!\/)/.test(apiBaseUrl)) ||
    /^https:\/\/[A-Za-z0-9.-]+(?::[0-9]+)?(?:\/|$)/.test(apiBaseUrl)
  )) throw new Error("apiBaseUrl must be a same-origin path or an explicit HTTPS origin");
  const keyId = asHash(key.keyId, "indexer keyId");
  const publicKeyBase64url = asString(key.publicKeyBase64url, "indexer public key");
  if (!/^[A-Za-z0-9_-]{43}$/.test(publicKeyBase64url)) {
    throw new Error("indexer public key must be canonical unpadded base64url");
  }
  return {
    schemaVersion: 1,
    apiBaseUrl,
    protocolManifestSha256: asHash(record.protocolManifestSha256, "runtime protocol manifest hash"),
    indexerSigningKey: {
      algorithm: "Ed25519",
      keyId,
      publicKeyBase64url,
    },
  };
}

export function asRecord(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value as Record<string, unknown>;
}

export function asString(value: unknown, name: string): string {
  if (typeof value !== "string" || value.length === 0) throw new TypeError(`${name} must be a string`);
  return value;
}

export function asHash(value: unknown, name: string): string {
  const hash = asString(value, name).toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(hash)) throw new TypeError(`${name} must be a SHA-256 hash`);
  return hash;
}

export function asTonAddress(value: unknown, name: string): string {
  const address = asString(value, name);
  try {
    return Address.parse(address).toString({ bounceable: true, urlSafe: true });
  } catch (error) {
    throw new TypeError(`${name} must be a TON address`, { cause: error });
  }
}

function asStringRecord(value: unknown, name: string): Record<string, string> {
  const input = asRecord(value, name);
  return Object.fromEntries(Object.entries(input).map(([key, item]) => [key, asHash(item, `${name}.${key}`)]));
}
